import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

function KotTaxProfileMaster({ token, selectedProperty, selectedOutletId = '' }) {
  const { isMobile } = useIsMobile();
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState(selectedOutletId || '');
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [rules, setRules] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    is_default: false,
    is_active: true,
    valid_from: '',
    valid_to: ''
  });
  const [ruleForm, setRuleForm] = useState({
    rule_name: '',
    tax_type: 'PERCENTAGE',
    tax_value: '',
    apply_on: 'ITEM',
    is_compound: false,
    display_order: 1,
    is_active: true
  });

  const chainId = selectedProperty?.chain_id ? String(selectedProperty.chain_id).trim() : '';
  const propertyId = selectedProperty?.id ? String(selectedProperty.id).trim() : '';
  const baseHeaders = { Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId };
  const scopedHeaders = (extra = {}) => ({ ...baseHeaders, 'x-outlet-id': outletId, ...extra });

  const clearStatus = () => {
    setError('');
    setMessage('');
  };

  const loadOutlets = async () => {
    if (!chainId || !propertyId) return;
    try {
      const res = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(propertyId)}`, { headers: baseHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load outlets');
      const all = Array.isArray(data) ? data : [];
      const rows = all.filter(o => String(o.outlet_type || '').toUpperCase() !== 'BAR' || o.module_kot_enabled === true);
      setOutlets(rows);
      const matchSelected = selectedOutletId && rows.some((r) => String(r.id) === String(selectedOutletId));
      setOutletId((prev) => (matchSelected ? selectedOutletId : (prev && rows.some((r) => String(r.id) === String(prev)) ? prev : (rows[0]?.id || ''))));
    } catch (err) {
      setOutlets([]);
      setOutletId('');
      setError(err?.message || 'Network error while loading outlets');
    }
  };

  const loadProfiles = async () => {
    if (!outletId) return setProfiles([]);
    try {
      const res = await fetch('/api/v1/kot/masters/tax-profiles', { headers: scopedHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load tax profiles');
      const rows = Array.isArray(data) ? data : [];
      setProfiles(rows);
      setSelectedProfileId((prev) => (prev && rows.some((r) => String(r.id) === String(prev)) ? prev : (rows[0]?.id || '')));
    } catch (err) {
      setProfiles([]);
      setSelectedProfileId('');
      setRules([]);
      setError(err?.message || 'Network error while loading tax profiles');
    }
  };

  const loadRules = async () => {
    if (!outletId || !selectedProfileId) return setRules([]);
    try {
      const res = await fetch(`/api/v1/kot/masters/tax-profiles/${selectedProfileId}/rules`, { headers: scopedHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load tax rules');
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      setRules([]);
      setError(err?.message || 'Network error while loading tax rules');
    }
  };

  useEffect(() => {
    setOutlets([]);
    setOutletId(selectedOutletId || '');
    setProfiles([]);
    setSelectedProfileId('');
    setRules([]);
    if (chainId && propertyId) loadOutlets();
  }, [chainId, propertyId, token]);

  useEffect(() => {
    if (selectedOutletId && outlets.some((o) => String(o.id) === String(selectedOutletId))) {
      setOutletId(selectedOutletId);
    }
  }, [selectedOutletId]);

  useEffect(() => {
    setProfiles([]);
    setSelectedProfileId('');
    setRules([]);
    if (outletId) loadProfiles();
  }, [outletId]);

  useEffect(() => {
    setRules([]);
    if (selectedProfileId) loadRules();
  }, [selectedProfileId]);

  const createProfile = async () => {
    clearStatus();
    if (!outletId) return setError('Select outlet first');
    if (!String(profileForm.name || '').trim()) return setError('Profile name is required');
    try {
      const payload = {
        ...profileForm,
        name: String(profileForm.name || '').trim(),
        description: String(profileForm.description || '').trim() || null,
        valid_from: profileForm.valid_from || null,
        valid_to: profileForm.valid_to || null
      };
      const res = await fetch('/api/v1/kot/masters/tax-profiles', {
        method: 'POST',
        headers: scopedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create tax profile');
      setMessage('Tax profile created');
      setProfileForm({ name: '', description: '', is_default: false, is_active: true, valid_from: '', valid_to: '' });
      await loadProfiles();
      setSelectedProfileId(data.id);
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  const addRule = async () => {
    clearStatus();
    if (!outletId || !selectedProfileId) return setError('Select profile first');
    if (!String(ruleForm.rule_name || '').trim()) return setError('Rule name is required');
    if (ruleForm.tax_value === '' || Number.isNaN(Number(ruleForm.tax_value))) return setError('Valid tax value required');
    try {
      const payload = {
        ...ruleForm,
        rule_name: String(ruleForm.rule_name || '').trim(),
        tax_value: Number(ruleForm.tax_value),
        display_order: Number(ruleForm.display_order || 1)
      };
      const res = await fetch(`/api/v1/kot/masters/tax-profiles/${selectedProfileId}/rules`, {
        method: 'POST',
        headers: scopedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to add rule');
      setMessage('Tax rule added');
      setRuleForm({ rule_name: '', tax_type: 'PERCENTAGE', tax_value: '', apply_on: 'ITEM', is_compound: false, display_order: 1, is_active: true });
      loadRules();
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  const inputStyle = { padding: '0.65rem', border: '1px solid #dcdde1', borderRadius: '6px' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>KOT Tax Profile Master</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div style={{ color: '#2f3640', marginBottom: '0.7rem' }}>{message}</div>}

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Scope</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.7rem' }}>
          <input readOnly value={selectedProperty ? `${selectedProperty.code || ''} - ${selectedProperty.name || ''}` : 'No property selected'} style={{ ...inputStyle, background: '#ecf0f1' }} />
          <select value={outletId} onChange={(e) => setOutletId(e.target.value)} style={inputStyle}>
            <option value="">Select Outlet</option>
            {outlets.map((o) => <option key={o.id} value={o.id}>{o.code} - {o.name}</option>)}
          </select>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Create Tax Profile</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.7rem' }}>
          <input placeholder="Profile Name" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} style={inputStyle} />
          <input placeholder="Description" value={profileForm.description} onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })} style={inputStyle} />
          <input type="date" value={profileForm.valid_from} onChange={(e) => setProfileForm({ ...profileForm, valid_from: e.target.value })} style={inputStyle} />
          <input type="date" value={profileForm.valid_to} onChange={(e) => setProfileForm({ ...profileForm, valid_to: e.target.value })} style={inputStyle} />
          <label><input type="checkbox" checked={profileForm.is_default} onChange={(e) => setProfileForm({ ...profileForm, is_default: e.target.checked })} /> Default</label>
          <label><input type="checkbox" checked={profileForm.is_active} onChange={(e) => setProfileForm({ ...profileForm, is_active: e.target.checked })} /> Active</label>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          <button onClick={createProfile} className="btn-primary" style={{ border: 'none', borderRadius: '6px', padding: '0.6rem 1rem', cursor: 'pointer' }}>Create Tax Profile</button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Profiles</h3>
        <select value={selectedProfileId} onChange={(e) => setSelectedProfileId(e.target.value)} style={{ ...inputStyle, minWidth: isMobile ? 0 : '320px' }}>
          <option value="">Select Tax Profile</option>
          {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_default ? ' (Default)' : ''}</option>)}
        </select>
      </div>

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Add Tax Rule</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.7rem' }}>
          <input placeholder="Rule Name (e.g. GST 5%)" value={ruleForm.rule_name} onChange={(e) => setRuleForm({ ...ruleForm, rule_name: e.target.value })} style={inputStyle} />
          <select value={ruleForm.tax_type} onChange={(e) => setRuleForm({ ...ruleForm, tax_type: e.target.value })} style={inputStyle}>
            <option value="PERCENTAGE">PERCENTAGE</option>
            <option value="FIXED">FIXED</option>
          </select>
          <input type="number" step="0.01" placeholder="Tax Value" value={ruleForm.tax_value} onChange={(e) => setRuleForm({ ...ruleForm, tax_value: e.target.value })} style={inputStyle} />
          <select value={ruleForm.apply_on} onChange={(e) => setRuleForm({ ...ruleForm, apply_on: e.target.value })} style={inputStyle}>
            <option value="ITEM">ITEM</option>
            <option value="BILL">BILL</option>
          </select>
          <input type="number" min="1" value={ruleForm.display_order} onChange={(e) => setRuleForm({ ...ruleForm, display_order: e.target.value })} style={inputStyle} />
          <label><input type="checkbox" checked={ruleForm.is_compound} onChange={(e) => setRuleForm({ ...ruleForm, is_compound: e.target.checked })} /> Compound</label>
          <label><input type="checkbox" checked={ruleForm.is_active} onChange={(e) => setRuleForm({ ...ruleForm, is_active: e.target.checked })} /> Active</label>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          <button onClick={addRule} className="btn-primary" style={{ border: 'none', borderRadius: '6px', padding: '0.6rem 1rem', cursor: 'pointer' }}>Add Rule</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Rule List</h3>
        {rules.length === 0 ? <p>No rules for selected profile</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Rule</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Value</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Apply On</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Compound</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.rule_name}</td>
                  <td style={{ padding: '0.6rem' }}>{r.tax_type}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(r.tax_value || 0).toFixed(2)}</td>
                  <td style={{ padding: '0.6rem' }}>{r.apply_on}</td>
                  <td style={{ padding: '0.6rem' }}>{r.is_compound ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default KotTaxProfileMaster;
